import { useEffect, useRef } from 'react';

/**
 * Run `callback` now, then every `intervalMs` while the tab is visible.
 *
 *   usePolling(fetchUsers, 5000, [page, search]);
 *
 * Every poll on this console is SQL on the admin-console, and a console left open in a
 * background tab used to cost exactly what one being read costs, once per open tab (S6).
 * A hidden tab now does not poll at all. Coming back to it runs the callback at once when
 * the last run is older than one interval, so a page somebody is looking at never shows
 * figures older than that.
 *
 * The latest `callback` is always the one called, so it may close over state freely. The
 * timer is rebuilt, with an immediate run, only when `intervalMs` or `deps` change (a new
 * page, a new filter). A falsy `intervalMs` turns the hook off: no call, no timer.
 */
export default function usePolling(callback, intervalMs, deps = []) {
  const latest = useRef(callback);
  latest.current = callback;

  useEffect(() => {
    if (!intervalMs) return undefined;
    let timer = null;
    let lastRun = 0;
    const run = () => {
      lastRun = Date.now();
      latest.current();
    };
    const start = () => {
      if (timer === null) timer = setInterval(run, intervalMs);
    };
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        stop();
        return;
      }
      if (Date.now() - lastRun >= intervalMs) run();
      start();
    };
    run();
    if (document.visibilityState !== 'hidden') start();
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVisibility);
    };
    // `deps` is the caller's dependency list, forwarded on purpose.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalMs, ...deps]);
}
